/**
 * Entitlements — the shared vocabulary and its readers.
 *
 * This package is imported by BOTH apps. It holds what the server and the browser must agree on:
 * the resource/constraint vocabulary, and the functions that read a resolved matrix.
 *
 * What it deliberately does NOT hold is the plan policies. Those live server-side in
 * `apps/gfgf-api/src/lib/entitlements/plans/`, because the server sends the browser a resolved
 * ANSWER and never the inputs — `max_recipients: 2000`, not `plan: "pro"` plus a rule table to
 * evaluate. A package `exports` map is per-package and not per-consumer, so anything published
 * here is publishable to the browser; keeping policies out of it is what makes that structural
 * rather than a convention.
 *
 * The throwing forms (`assertAllows`, `assertWithin`, …) also stay server-side: they raise
 * EntitlementError, which is an HTTP concern, and nothing in the browser throws an entitlement.
 */

import type {
  FlagConstraint,
  PlanTemplate,
  Quantity,
  QuantityConstraint,
  ResolvedEntitlements,
  Resource,
  SetConstraint,
  SetMember,
} from "./types";

// Plan display copy — static, and needed by any surface that sells a plan, including one that
// renders before the caller has an account. See ./display.ts for why it is not fetched.
export * from "./display";
export * from "./types";

/** A plan's declared policy as a resolved bag. Per-organization overrides apply on top. */
export function fromTemplate(template: PlanTemplate): ResolvedEntitlements {
  return {
    plan: { key: template.key, version: template.version, label: template.label },
    entitlements: template.entitlements,
  };
}

/** True if `member` is one of the variants the plan grants for (resource, constraint). */
export function allows<R extends Resource, C extends SetConstraint<R>>(
  ent: ResolvedEntitlements,
  resource: R,
  constraint: C,
  member: SetMember<R, C>,
): boolean {
  const set = ent.entitlements[resource][constraint] as readonly unknown[];
  return set.includes(member);
}

/** Whether a flag capability is granted. */
export function enabled<R extends Resource, C extends FlagConstraint<R>>(
  ent: ResolvedEntitlements,
  resource: R,
  constraint: C,
): boolean {
  return ent.entitlements[resource][constraint] === true;
}

/** The cap for (resource, constraint). `null` means unlimited — never "zero" and never "unset". */
export function limit<R extends Resource, C extends QuantityConstraint<R>>(
  ent: ResolvedEntitlements,
  resource: R,
  constraint: C,
): Quantity {
  return ent.entitlements[resource][constraint] as Quantity;
}

/**
 * Whether `n` fits under the cap. `null` (unlimited) always fits; a cap of 0 admits only 0, which
 * is what makes `campaign_reminders.max: 0` mean "no reminders at all" rather than "unset".
 */
export function within<R extends Resource, C extends QuantityConstraint<R>>(
  ent: ResolvedEntitlements,
  resource: R,
  constraint: C,
  n: number,
): boolean {
  const cap = limit(ent, resource, constraint);
  return cap === null || n <= cap;
}

/**
 * Whether the plan grants ANY variant of a set constraint — "may this organization edit
 * collections at all?", as distinct from which edits it may make. Some payload fields record
 * that a capability was used without recording which variant (an edited product feed is one
 * blob, not a list of reorder/add/remove operations), so the only answerable question is
 * whether the set is empty.
 */
export function hasAny<R extends Resource, C extends SetConstraint<R>>(
  ent: ResolvedEntitlements,
  resource: R,
  constraint: C,
): boolean {
  return (ent.entitlements[resource][constraint] as readonly unknown[]).length > 0;
}

/**
 * The cap clamped to a concrete number for callers that must produce a bounded value (the clone
 * path truncating a recipient list, a UI cap). Unlimited resolves to `fallback`.
 */
export function cap<R extends Resource, C extends QuantityConstraint<R>>(
  ent: ResolvedEntitlements,
  resource: R,
  constraint: C,
  fallback: number,
): number {
  return limit(ent, resource, constraint) ?? fallback;
}
